/**
 * Shared severity → color palette for view profiles.
 *
 * Profile rules declare a `severity`; the legend / canvas reads color from
 * this palette. Centralizing here keeps the visual language consistent
 * across all domains (network security, network cost, future GRC views,
 * etc.) and lets us swap themes (e.g. dark mode) in one place.
 *
 * Rules MAY still set an explicit `color` to override per-rule when a
 * domain-specific signal is more useful than severity (e.g. cost's "low"
 * bucket using green instead of grey to read as "fine"), but the default
 * is to inherit from severity.
 */
import type { Severity } from './ScoringEngine';

export interface SeverityColor {
  /** Light-mode hex (canvas default). */
  light: string;
  /** Dark-mode hex (used when `prefers-color-scheme: dark` is active). */
  dark: string;
}

// Light values picked to clear WCAG AA 3:1 minimum on a white panel
// background (the default `ProfileLegend` surface). The previous low/info
// values (#a3a3a3, #64748b) were borderline-to-failing for non-text UI;
// darkened to #6b7280 / #475569 (≈4.6:1 / 7.6:1).
export const SEVERITY_PALETTE: Record<Severity, SeverityColor> = {
  critical: { light: '#dc2626', dark: '#f87171' },
  high:     { light: '#ea580c', dark: '#fb923c' },
  medium:   { light: '#b45309', dark: '#facc15' }, // amber-700 (was #ca8a04, ≈3.0:1 borderline)
  low:      { light: '#6b7280', dark: '#a3a3a3' }, // gray-500 (was #a3a3a3, ≈2.6:1 fail)
  info:     { light: '#475569', dark: '#94a3b8' }, // slate-600 (was #64748b, ≈4.6:1 borderline)
};

/**
 * Resolve the color for a severity. Defaults to light mode; pass `'dark'`
 * to get the dark-mode swatch.
 */
export function severityColor(severity: Severity, theme: 'light' | 'dark' = 'light'): string {
  return SEVERITY_PALETTE[severity][theme];
}
